import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormRow from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/LinkResource/FormRow'
import Loader from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/LinkResource/Loader'
import ModalContent from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/LinkResource/ModalContent'
import OptionalLabel from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/LinkResource/OptionalLabel'
import Sidebar from 'shared/components/ui/Sidebar'
import ValidatedField from 'shared/components/ui/ValidatedField'
import { Button, Flex, Label, Notification, Tooltip } from 'shared/ui'
import { Dropdown, Form, FormTextArea, Input } from 'util/ui'

import {
  doCreateAndLinkLinearIssueToTicket,
  doFetchLinearAssignees,
  doFetchLinearLabels,
  doFetchLinearProjects,
  doFetchLinearWorkflowStates,
  doRetryLinearIssueLinkToTicket,
  isLinearIssueLinkRecovery,
  selectLinearTeamsAsOptions,
  selectLinearTeamsState,
  selectLinearWorkflowStatesAsOptionsForTeam,
  selectLinearWorkflowStatesByTeamId,
} from 'ducks/integrations/linear'
import { claimPending } from '../pending'

export const LINEAR_NEW_ISSUE_CREATE_FAILURE_HEADER = 'Create failed'
export const LINEAR_NEW_ISSUE_CREATE_FAILURE_MESSAGE =
  'Could not create the Linear issue. Try again.'
export const LINEAR_NEW_ISSUE_PARTIAL_FAILURE_HEADER = 'Issue created'
export const LINEAR_NEW_ISSUE_PARTIAL_FAILURE_MESSAGE =
  'Issue created in Linear, but Groove could not link it to this conversation. Retry linking.'
export const LINEAR_NEW_ISSUE_RETRY_FAILURE_HEADER = 'Link failed'
export const LINEAR_NEW_ISSUE_RETRY_FAILURE_MESSAGE =
  'Could not link the created Linear issue. Retry linking.'

const EMPTY_LOOKUPS = {
  assignees: [],
  labels: [],
  projects: [],
}

function toIssueOption(item) {
  return {
    key: item.id,
    value: item.id,
    text: item.name || item.displayName || item.id,
  }
}

export function buildLinearIssueCreateInput({
  assigneeId,
  description,
  labelIds,
  projectId,
  teamId,
  title,
  workflowStateId,
}) {
  const input = {
    stateId: workflowStateId,
    teamId,
    title,
  }

  if (description?.trim()) input.description = description.trim()
  if (assigneeId) input.assigneeId = assigneeId
  if (projectId) input.projectId = projectId
  if (labelIds?.length) input.labelIds = labelIds

  return input
}

export function getLinkNewLinearIssueControls({
  pending,
  recovery,
  teamId,
  teamsState,
  title,
  workflowStateId,
  workflowStateState,
}) {
  return {
    isTeamsLoading: !teamsState?.loaded || teamsState?.loading,
    isWorkflowStatesLoading:
      !!teamId && (!workflowStateState?.loaded || workflowStateState?.loading),
    isReady: !!teamId && !!workflowStateId && title.trim().length > 0,
    showRetry: !!recovery,
    createDisabled:
      !teamId || !workflowStateId || title.trim().length === 0 || !!pending,
    retryDisabled: !!pending,
  }
}

export function getLinkNewLinearIssueCreateFailureFeedback() {
  return {
    header: LINEAR_NEW_ISSUE_CREATE_FAILURE_HEADER,
    message: LINEAR_NEW_ISSUE_CREATE_FAILURE_MESSAGE,
    type: 'negative',
  }
}

export function getLinkNewLinearIssuePartialFailureFeedback() {
  return {
    header: LINEAR_NEW_ISSUE_PARTIAL_FAILURE_HEADER,
    message: LINEAR_NEW_ISSUE_PARTIAL_FAILURE_MESSAGE,
    type: 'warning',
  }
}

export function getLinkNewLinearIssueRetryFailureFeedback() {
  return {
    header: LINEAR_NEW_ISSUE_RETRY_FAILURE_HEADER,
    message: LINEAR_NEW_ISSUE_RETRY_FAILURE_MESSAGE,
    type: 'negative',
  }
}

export default function LinkNewLinearIssueModal() {
  const dispatch = useDispatch()
  const teams = useSelector(selectLinearTeamsAsOptions)
  const teamsState = useSelector(selectLinearTeamsState)
  const [open, setOpen] = useState(false)
  const [teamId, setTeamId] = useState('')
  const [workflowStateId, setWorkflowStateId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [labelIds, setLabelIds] = useState([])
  const [pending, setPending] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [recovery, setRecovery] = useState(null)
  const [lookups, setLookups] = useState(EMPTY_LOOKUPS)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupError, setLookupError] = useState(null)
  const pendingRef = useRef(false)
  const lookupRequestRef = useRef(0)

  const workflowStateState = useSelector(state =>
    selectLinearWorkflowStatesByTeamId(state, teamId)
  )
  const workflowStates = useSelector(state =>
    selectLinearWorkflowStatesAsOptionsForTeam(state, teamId)
  )

  const releasePending = useCallback(() => {
    pendingRef.current = false
    setPending(false)
  }, [])

  const resetInteractionState = useCallback(() => {
    setFeedback(null)
    setRecovery(null)
  }, [])

  const resetTeamDependentFields = useCallback(() => {
    setWorkflowStateId('')
    setAssigneeId('')
    setProjectId('')
    setLabelIds([])
    setLookups(EMPTY_LOOKUPS)
    setLookupLoading(false)
    setLookupError(null)
  }, [])

  const completeClose = useCallback(
    () => {
      setOpen(false)
      setTeamId('')
      setTitle('')
      setDescription('')
      resetTeamDependentFields()
      resetInteractionState()
    },
    [resetInteractionState, resetTeamDependentFields]
  )

  const handleDismiss = useCallback(
    () => {
      if (pendingRef.current) return
      completeClose()
    },
    [completeClose]
  )

  const close = useCallback(() => completeClose(), [completeClose])
  const show = useCallback(() => setOpen(true), [])

  const handleTeamChange = useCallback(
    (_, { value }) => {
      setTeamId(value)
      resetTeamDependentFields()
      if (value) {
        dispatch(doFetchLinearWorkflowStates(value))
      }
    },
    [dispatch, resetTeamDependentFields]
  )

  const handleWorkflowStateChange = useCallback(
    (_, { value }) => setWorkflowStateId(value),
    []
  )
  const handleTitleChange = useCallback((_, { value }) => setTitle(value), [])
  const handleDescriptionChange = useCallback(
    (_, { value }) => setDescription(value),
    []
  )
  const handleAssigneeChange = useCallback(
    (_, { value }) => setAssigneeId(value),
    []
  )
  const handleProjectChange = useCallback(
    (_, { value }) => setProjectId(value),
    []
  )
  const handleLabelsChange = useCallback(
    (_, { value }) => setLabelIds(value || []),
    []
  )

  useEffect(
    () => {
      if (!teamId) return undefined

      const requestId = ++lookupRequestRef.current
      setLookupLoading(true)
      setLookupError(null)

      Promise.all([
        dispatch(doFetchLinearAssignees(teamId)),
        dispatch(doFetchLinearLabels(teamId)),
        dispatch(doFetchLinearProjects(teamId)),
      ])
        .then(([assignees, labels, projects]) => {
          if (lookupRequestRef.current !== requestId) return
          setLookups({
            assignees: assignees || [],
            labels: labels || [],
            projects: projects || [],
          })
        })
        .catch(error => {
          if (lookupRequestRef.current !== requestId) return
          setLookups(EMPTY_LOOKUPS)
          setLookupError(error)
        })
        .finally(() => {
          if (lookupRequestRef.current !== requestId) return
          setLookupLoading(false)
        })

      return () => {
        lookupRequestRef.current += 1
      }
    },
    [dispatch, teamId]
  )

  const {
    createDisabled,
    isTeamsLoading,
    isWorkflowStatesLoading,
    isReady,
    retryDisabled,
    showRetry,
  } = getLinkNewLinearIssueControls({
    pending,
    recovery,
    teamId,
    teamsState,
    title,
    workflowStateId,
    workflowStateState,
  })

  const lookupOptions = useMemo(
    () => ({
      assignees: (lookups.assignees || []).map(toIssueOption),
      labels: (lookups.labels || []).map(toIssueOption),
      projects: (lookups.projects || []).map(toIssueOption),
    }),
    [lookups]
  )

  const handleCreate = useCallback(
    () => {
      if (!claimPending(pendingRef)) return
      setFeedback(null)
      setRecovery(null)
      setPending(true)
      dispatch(
        doCreateAndLinkLinearIssueToTicket(
          buildLinearIssueCreateInput({
            assigneeId,
            description,
            labelIds,
            projectId,
            teamId,
            title: title.trim(),
            workflowStateId,
          })
        )
      )
        .then(result => {
          if (isLinearIssueLinkRecovery(result)) {
            setRecovery(result)
            setFeedback(getLinkNewLinearIssuePartialFailureFeedback())
            return
          }
          close()
        })
        .catch(() => {
          setFeedback(getLinkNewLinearIssueCreateFailureFeedback())
        })
        .finally(() => {
          releasePending()
        })
    },
    [
      assigneeId,
      close,
      description,
      dispatch,
      labelIds,
      projectId,
      releasePending,
      teamId,
      title,
      workflowStateId,
    ]
  )

  const handleRetryLink = useCallback(
    () => {
      if (!claimPending(pendingRef) || !recovery) return
      setFeedback(null)
      setPending(true)
      dispatch(doRetryLinearIssueLinkToTicket(recovery))
        .then(() => {
          close()
        })
        .catch(() => {
          setFeedback(getLinkNewLinearIssueRetryFailureFeedback())
        })
        .finally(() => {
          releasePending()
        })
    },
    [close, dispatch, recovery, releasePending]
  )

  const footerChildren = useMemo(
    () => [
      showRetry ? (
        <Button
          key="retry"
          disabled={retryDisabled}
          fluid
          onClick={handleRetryLink}
          primary
        >
          Retry linking
        </Button>
      ) : (
        <Tooltip
          key="create"
          portal
          position="top"
          tooltip={!isReady && 'Choose team, state, and enter issue title'}
        >
          <Button
            disabled={createDisabled}
            fluid
            onClick={handleCreate}
            primary
          >
            Create and link issue
          </Button>
        </Tooltip>
      ),
      <Button
        basic
        fluid
        key="cancel"
        className="grui mt-4"
        onClick={handleDismiss}
        disabled={pending}
      >
        Cancel
      </Button>,
    ],
    [
      createDisabled,
      handleCreate,
      handleDismiss,
      handleRetryLink,
      isReady,
      pending,
      retryDisabled,
      showRetry,
    ]
  )

  return (
    <React.Fragment>
      <Button basic onClick={show} className="grui ml-4 mt-4">
        New
      </Button>
      <Sidebar
        footerChildren={footerChildren}
        headerChildren="Link to New Linear Issue"
        onClose={handleDismiss}
        open={open}
        wide
      >
        <ModalContent>
          {feedback && (
            <Notification
              fluid
              header={feedback.header}
              message={feedback.message}
              type={feedback.type}
            />
          )}
          <Form>
            {isTeamsLoading ? (
              <Loader />
            ) : (
              <React.Fragment>
                <Flex>
                  <FormRow split>
                    <Label as="div">Team</Label>
                    <ValidatedField
                      fluid
                      name="team"
                      options={teams}
                      onChange={handleTeamChange}
                      placeholder="- Please select -"
                      search
                      selection
                      selectOnNavigation={false}
                      validatedFieldComponent={Dropdown}
                      value={teamId}
                    />
                  </FormRow>
                  <FormRow split>
                    <Label as="div">Workflow state</Label>
                    <ValidatedField
                      disabled={!teamId || isWorkflowStatesLoading}
                      fluid
                      name="workflowState"
                      options={workflowStates}
                      onChange={handleWorkflowStateChange}
                      placeholder="- Please select -"
                      search
                      selection
                      selectOnNavigation={false}
                      validatedFieldComponent={Dropdown}
                      value={workflowStateId}
                    />
                  </FormRow>
                </Flex>
                <FormRow>
                  <Label as="div">Issue title</Label>
                  <ValidatedField
                    disabled={!teamId}
                    fluid
                    name="title"
                    onChange={handleTitleChange}
                    validatedFieldComponent={Input}
                    value={title}
                  />
                </FormRow>
                <FormRow>
                  <Label as="div">
                    Description&nbsp;<OptionalLabel />
                  </Label>
                  <ValidatedField
                    disabled={!teamId}
                    fluid
                    name="description"
                    onChange={handleDescriptionChange}
                    placeholder="Add details, links, or context"
                    rows={4}
                    validatedFieldComponent={FormTextArea}
                    value={description}
                  />
                </FormRow>
                {lookupError && (
                  <Notification
                    fluid
                    header="Could not load team data"
                    message="Try selecting the team again."
                    type="negative"
                  />
                )}
                <Flex>
                  <FormRow split>
                    <Label as="div">
                      Assignee&nbsp;<OptionalLabel />
                    </Label>
                    <ValidatedField
                      disabled={!teamId || lookupLoading}
                      fluid
                      loading={lookupLoading}
                      name="assignee"
                      noResultsMessage={
                        lookupLoading ? 'Loading...' : 'No assignees found'
                      }
                      options={lookupOptions.assignees}
                      onChange={handleAssigneeChange}
                      placeholder={
                        lookupLoading ? 'Loading...' : '- Please select -'
                      }
                      search
                      selection
                      selectOnNavigation={false}
                      validatedFieldComponent={Dropdown}
                      value={assigneeId}
                    />
                  </FormRow>
                  <FormRow split>
                    <Label as="div">
                      Project&nbsp;<OptionalLabel />
                    </Label>
                    <ValidatedField
                      disabled={!teamId || lookupLoading}
                      fluid
                      loading={lookupLoading}
                      name="project"
                      noResultsMessage={
                        lookupLoading ? 'Loading...' : 'No projects found'
                      }
                      options={lookupOptions.projects}
                      onChange={handleProjectChange}
                      placeholder={
                        lookupLoading ? 'Loading...' : '- Please select -'
                      }
                      search
                      selection
                      selectOnNavigation={false}
                      validatedFieldComponent={Dropdown}
                      value={projectId}
                    />
                  </FormRow>
                </Flex>
                <FormRow>
                  <Label as="div">
                    Labels&nbsp;<OptionalLabel />
                  </Label>
                  <ValidatedField
                    disabled={!teamId || lookupLoading}
                    fluid
                    loading={lookupLoading}
                    multiple
                    name="labels"
                    noResultsMessage={
                      lookupLoading ? 'Loading...' : 'No labels found'
                    }
                    options={lookupOptions.labels}
                    onChange={handleLabelsChange}
                    placeholder={
                      lookupLoading ? 'Loading...' : '- Please select -'
                    }
                    search
                    selection
                    selectOnNavigation={false}
                    validatedFieldComponent={Dropdown}
                    value={labelIds}
                  />
                </FormRow>
              </React.Fragment>
            )}
          </Form>
        </ModalContent>
      </Sidebar>
    </React.Fragment>
  )
}
